import {
  useFieldArray,
  UseFieldArrayMethods,
  useForm,
  UseFormMethods,
  useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import * as ReactRedux from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateBuyOrder } from 'services/buy-process';
import { useBuyProcessForm } from '../../buy-process-form-utils';
import { RootState } from '../../../../global';
import { RadioConvertUtils } from '../../../../utils/radio/radio';
import {
  deleteBuyOrderOrganization,
  saveEditOwnerShares,
} from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import { useLegalShareholders } from '../../../admin/buy-process/organizations/use-organizations';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useSaveBuyOrderFormState,
} from '../../use-buy-process-form';
import { LegalShareholderEntity } from '../../../../global/entities/Person';
import { isPresent } from '../../../../utils/is-present';
import { hasFieldsWithValues } from '../../../../utils';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import { selectBuyPhysicalPeopleFactory } from '../../../../services/buy-process/selectors';
import {
  isNumberSharesValid,
  ownerSharesValidationSchema,
} from './owner-shares-utils';
import { sumTotalShares } from './components/remaining-shares-label/remaining-shares-label-utils';
import {
  OwnerSharesFormState,
  OwnerSharesMetaFields,
} from './owner-shares-types';

type FormData = OwnerSharesFormState & OwnerSharesMetaFields;

function filterShareholdersWithValues(data: FormData): FormData {
  const physicalPeople = data.physicalPeople?.filter(hasFieldsWithValues);
  const legalShareholders = data.legalShareholders?.filter(hasFieldsWithValues);

  return {
    ...data,
    physicalPeople,
    legalShareholders,
  };
}

export const useOwnerShares = () => {
  const PHYSICAL_PEOPLE = 'physicalPeople';
  const LEGAL_SHAREHOLDERS = 'legalShareholders';

  const { setRequestedStep, id } = useBuyProcessForm();

  const { numberShares, beneficialOwnerHelp, shareCapital } = ReactRedux.useSelector(
    (state: RootState) => state.buyOrderEdit[id].ownerShares,
  );

  const physicalPeople = useSelector(selectBuyPhysicalPeopleFactory(id));
  const legalShareholders = useLegalShareholders(id);

  const getPhysicalPeople = () => {
    if (physicalPeople.length > 0) {
      return physicalPeople;
    }

    if (legalShareholders.length > 0) {
      return [];
    }

    return [{}];
  };

  const formMethods = useForm<FormData>({
    resolver: yupResolver(ownerSharesValidationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      shareCapital,
      legalShareholders,
      physicalPeople: getPhysicalPeople(),
      beneficialOwnerHelp: RadioConvertUtils.boolToStr(beneficialOwnerHelp),
      numberShares: isNumberSharesValid(numberShares)
        ? (String(numberShares) as any)
        : '250',
    },
  });
  const { register } = formMethods;

  useSaveBuyOrderFormState(
    saveEditOwnerShares,
    filterShareholdersWithValues(formMethods.getValues()),
    formMethods.trigger,
  );

  const physicalPeopleFieldArray = useFieldArray({
    control: formMethods.control,
    name: PHYSICAL_PEOPLE,
  });

  const legalShareholdersFieldArray = useFieldArray({
    control: formMethods.control,
    name: LEGAL_SHAREHOLDERS,
  });

  useEffect(() => {
    // We must manually register `shareCapital` because it is calculated,
    // not `input`ed from an input component (so we can't `ref` it).
    register('shareCapital');
  }, [register]);

  const dispatch = useDispatch();
  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    physicalPeopleFieldArrayName: PHYSICAL_PEOPLE,
    legalShareholdersFieldArrayName: LEGAL_SHAREHOLDERS,
    physicalPeopleFieldArray,
    legalShareholdersFieldArray,
    formMethods,
    id,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(saveEditOwnerShares({ ...data, id }));
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, formMethods.trigger, e),
  };
};

/** `useWatch` causes a re-render whenever a *"watched"* value changes,
 *  so we can't leave it in a top-level component like `BuyProcessOwnerShares`.
 *
 *  [Video](https://www.youtube.com/watch?v=LhIvi4XHIco&t=892s) demonstrating the idea. */
export const useRemainingShares = (control: UseFormMethods['control']) => {
  const { legalShareholders, physicalPeople, numberShares } = useWatch({
    control,
    name: ['legalShareholders', 'physicalPeople', 'numberShares'],
    defaultValue: {
      physicalPeople: [] as any,
      legalShareholders: [] as any,
      numberShares: 250,
    },
  });

  return (
    Number(numberShares)
    - (sumTotalShares(physicalPeople) + sumTotalShares(legalShareholders))
  );
};

/**
 * If someone clicks on the "No" button in whether they want
 * legal shareholders or not, we use this to remove the legal shareholders
 * both from redux store, and from the form data.
 * */
export function useResetLegalShareholders(
  showLegalShareholders: boolean,
  legalShareholders: LegalShareholderEntity[] | undefined,
  remove: UseFieldArrayMethods['remove'],
  id: string,
) {
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!showLegalShareholders) {
      if (legalShareholders) {
        const organizationIds = legalShareholders
          .map((shareholder) => shareholder.organizationId)
          .filter((id) => id !== '')
          .filter(isPresent);

        dispatch(
          deleteBuyOrderOrganization({
            id,
            organizationId: organizationIds,
          }),
        );

        legalShareholders.forEach((_, index) => {
          remove(index);
        });
      }
    }
  }, [dispatch, id, legalShareholders, remove, showLegalShareholders]);
}
